import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/**
 * Outlined and flat by default.
 *
 * On a warm paper ground a drop shadow reads as grime, so depth comes from
 * tone and outline. `raised` exists for the few things that genuinely float —
 * the job at the top of the run sheet, the bar pinned over scrolling content.
 */
const card = cva('rounded-xl border', {
  variants: {
    variant: {
      default: 'border-line bg-raised',
      sunken: 'border-line bg-sunken',
      raised: 'border-line bg-raised shadow-md',
      wash: 'border-line-brand/25 bg-wash',
      danger: 'border-error-line/60 bg-error-surface',
      warning: 'border-warning-line/60 bg-warning-surface',
      ghost: 'border-transparent bg-transparent',
    },
    interactive: {
      true: [
        'transition-[border-color,box-shadow,transform]',
        'duration-[var(--motion-duration-base)] ease-standard',
        'hover:border-line-bold hover:shadow-md',
        'focus-within:border-line-brand',
        'active:scale-[0.995]',
      ],
      false: '',
    },
  },
  defaultVariants: { variant: 'default', interactive: false },
});

/**
 * `asChild` renders the card's styling onto the child element instead of a
 * div, for the cases where the semantics matter more than the box: a card that
 * IS a link, a list, or a description list. `dt` and `dd` inside a bare div
 * are not a description list at all — a screen reader hears a run of orphaned
 * text rather than label-and-value pairs.
 */
export function Card({
  className,
  variant,
  interactive,
  asChild = false,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof card> & { asChild?: boolean }) {
  const Component = asChild ? Slot : 'div';
  return <Component className={cn(card({ variant, interactive }), className)} {...props} />;
}

export function CardBody({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('p-4', className)} {...props} />;
}

/** A divider between rows inside one card. */
export function CardDivider({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('h-px bg-line', className)} role="presentation" {...props} />;
}
